/**
 * SuggestionRow — compact + expanded row for the suggestion lists
 * (Block 6.C.2 / ADR-021; Block 6.F.5 §10 sage refresh).
 *
 * Used by both SuggestionsQueue (steward) and MySuggestions (own).
 * Compact: target_display + field_name + old→new preview + status pip +
 * suggester (queue) or reviewer (own disposed). Tap to expand → child
 * pane (SuggestionReviewPane for stewards; MySuggestionDetailPane for
 * own).
 *
 * 6.F.5 §10 — sage palette refresh:
 *   * Container: hairline borders (tokened at UX-6).
 *   * Status pips: sage / warm-orange / red (true error) / stone (per
 *     6.F.4 §3.7 color discipline — red ONLY for actually-error states).
 *     "rejected" stays red (rejection IS an unfavorable outcome from
 *     the suggester's POV; not error-class, but the pip color carries
 *     the "this didn't go through" signal).
 *   * Focus ring: sage-500 (was blue-500).
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { SuggestedEditWithJoinDisplay } from '@/lib/types';
import { humanizeFieldName, humanizeFieldValue } from '@/lib/fieldDisplay';

export interface SuggestionRowProps {
  suggestion: SuggestedEditWithJoinDisplay;
  /** Variant determines which info shown on the compact row + which pane to expand. */
  variant: 'queue' | 'mine';
  /** Render the appropriate expanded pane. */
  renderExpanded: () => ReactNode;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-warm-500',
  approved: 'bg-sage-600',
  rejected: 'bg-sand0',
  withdrawn: 'bg-sage-300',
};

export function SuggestionRow({
  suggestion,
  variant,
  renderExpanded,
}: SuggestionRowProps): ReactNode {
  const [expanded, setExpanded] = useState(false);

  const statusColor = STATUS_COLORS[suggestion.status] ?? 'bg-sage-300';
  const compactSecondary =
    variant === 'queue'
      ? `${suggestion.suggester_display} · ${formatRelativeTime(suggestion.created_at)}`
      : `${formatRelativeTime(suggestion.created_at)}`;

  return (
    <div className="border border-sage-800/[8%] rounded-card mb-2 bg-paper shadow-sh1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`${suggestion.target_display} · ${humanizeFieldName(suggestion.target_type, suggestion.field_name)} (tap to ${expanded ? 'collapse' : 'expand'})`}
        className="w-full text-left px-3 py-2 min-h-11 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-sage-800 truncate">{suggestion.target_display}</div>
          <div className="text-xs text-ink-soft mt-0.5 truncate">
            <span className="font-medium text-sage-700">{humanizeFieldName(suggestion.target_type, suggestion.field_name)}</span>
            <span className="mx-1 text-sage-300">·</span>
            <span className="text-ink-soft">
              {humanizeFieldValue(suggestion.old_value, {
                targetType: suggestion.target_type,
                fieldName: suggestion.field_name,
              })}{' '}
              →{' '}
              {humanizeFieldValue(suggestion.new_value, {
                targetType: suggestion.target_type,
                fieldName: suggestion.field_name,
              })}
            </span>
          </div>
          <div className="text-xs text-ink-soft mt-0.5">
            <span
              aria-label={suggestion.status}
              className={`inline-block w-2 h-2 rounded-full mr-1 ${statusColor}`}
            />
            {suggestion.status} · {compactSecondary}
          </div>
        </div>
        {expanded ? (
          <ChevronUp size={18} aria-hidden="true" className="text-sage-300 shrink-0" />
        ) : (
          <ChevronDown size={18} aria-hidden="true" className="text-sage-300 shrink-0" />
        )}
      </button>

      {expanded && renderExpanded()}
    </div>
  );
}

function formatRelativeTime(iso: string | null): string {
  if (iso === null) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
